import { Sentry } from '@/shared/lib/sentry'

export type AnamnesisTechnicalEventName =
  | 'microphone_start'
  | 'microphone_stop'
  | 'microphone_permission_denied'
  | 'microphone_error'
  | 'streaming_restart'
  | 'streaming_max_session'
  | 'transcription_engine_selected'
  | 'transcription_engine_switched'
  | 'transcription_chunk_confirmed'
  | 'transcription_fallback_used'
  | 'transcription_provider_failed'
  | 'transcription_strategy_selected'
  | 'transcription_local_model_progress'
  | 'transcription_local_resource_decision'
  | 'transcript_block_confirmed'
  | 'speaker_classified'
  | 'speaker_low_confidence'
  | 'speaker_correction_applied'
  | 'diarization_failed'
  | 'speaker_verification_failed'
  | 'transcription_session_degraded'
  | 'external_whisper_upstream'
  | 'ai_insight_requested'
  | 'ai_insight_completed'
  | 'ai_insight_failed'
  | 'final_summary_requested'
  | 'final_summary_completed'
  | 'final_summary_failed'
  | 'persistence_completed'
  | 'persistence_failed'

export type AnamnesisTelemetryPayload = Record<string, string | number | boolean | null | undefined>

const ALLOWED_PAYLOAD_KEYS = new Set([
  'analysisRunCount',
  'approximateDeviceMemoryGb',
  'batteryLowPowerModeLikely',
  'blockCount',
  'browser',
  'capabilityScore',
  'capabilityTier',
  'cacheState',
  'chunkChars',
  'chunkId',
  'consultationId',
  'confidenceScore',
  'confidence',
  'device',
  'durationMs',
  'edgeReady',
  'engine',
  'engineAttempt',
  'errorCode',
  'errorName',
  'eventCount',
  'fieldCount',
  'fallbackProvider',
  'fromProvider',
  'isSafariLike',
  'isIOSLike',
  'localReady',
  'latencyMs',
  'maxSessionMs',
  'modelSizeBytes',
  'modelVersion',
  'os',
  'provider',
  'progressPercent',
  'queuedAnalysisCount',
  'reason',
  'resourceStatus',
  'semanticBlockCount',
  'semanticBlockId',
  'blockRevision',
  'previousSpeakerKind',
  'newSpeakerKind',
  'incorrectAutoGuess',
  'sequence',
  'sessionAgeMs',
  'speakerKind',
  'diarizationConfidence',
  'diarizationBackendKind',
  'doctorVoiceMatchScore',
  'speakerVerificationKind',
  'latencyBucket',
  'processingMs',
  'status',
  'suggestionCount',
  'sourceProvider',
  'switchId',
  'toProvider',
  'trigger',
  'upstreamOutcome',
  'wasmSupported',
  'webGpuSupported',
])

const SENSITIVE_KEY_PATTERN =
  /(text|transcript|complaint|conduct|patient|name|email|phone|document|cpf|cnpj|summary|answer|question)/i

const SAFE_STRING_VALUE_PATTERN = /^[a-z0-9._:-]+$/i

const sanitizeTelemetryStringValue = (value: string): string | null => {
  const cleanValue = value.trim()
  if (!cleanValue || cleanValue.length > 96 || !SAFE_STRING_VALUE_PATTERN.test(cleanValue)) {
    return null
  }
  return cleanValue
}

const getBrowserFamily = (): string => {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (/Edg\//i.test(ua)) return 'edge'
  if (/Chrome|CriOS/i.test(ua)) return 'chrome'
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox'
  if (/Safari/i.test(ua)) return 'safari'
  return 'other'
}

const getDeviceFamily = (): string => {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (/iPad|Tablet/i.test(ua)) return 'tablet'
  if (/Mobi|iPhone|Android/i.test(ua)) return 'mobile'
  return 'desktop'
}

export const getAnamnesisTelemetryEnvironment = () => ({
  browser: getBrowserFamily(),
  device: getDeviceFamily(),
})

export const sanitizeAnamnesisTelemetryPayload = (
  payload: AnamnesisTelemetryPayload = {},
): Record<string, string | number | boolean | null> => {
  const sanitized: Record<string, string | number | boolean | null> = {}
  Object.entries(payload).forEach(([key, value]) => {
    if (!ALLOWED_PAYLOAD_KEYS.has(key) || SENSITIVE_KEY_PATTERN.test(key)) return
    if (value == null || typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value == null ? null : value
      return
    }
    if (typeof value === 'string') {
      const cleanValue = sanitizeTelemetryStringValue(value)
      if (cleanValue != null) sanitized[key] = cleanValue
    }
  })
  return sanitized
}

export const trackAnamnesisTechnicalEvent = (
  name: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  const data = sanitizeAnamnesisTelemetryPayload({
    ...getAnamnesisTelemetryEnvironment(),
    ...payload,
  })
  try {
    Sentry.addBreadcrumb?.({
      category: 'anamnesis.technical',
      message: name,
      level: name.endsWith('_failed') || name.includes('error') || name.includes('denied') ? 'warning' : 'info',
      data,
    })
    void import('@/shared/observability/frontend-observability')
      .then(({ mirrorAnamnesisPipelineEvent }) => mirrorAnamnesisPipelineEvent(name, payload))
      .catch(() => {})
  } catch {
    // Telemetria técnica não deve interferir na consulta.
  }
}

export const reportAnamnesisTechnicalFailure = (
  error: unknown,
  eventName: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  const data = sanitizeAnamnesisTelemetryPayload({
    ...getAnamnesisTelemetryEnvironment(),
    ...payload,
    errorName: error instanceof Error ? error.name : typeof error,
  })
  trackAnamnesisTechnicalEvent(eventName, data)
  try {
    Sentry.captureException?.(error, {
      tags: {
        feature: 'anamnesis-realtime',
        event: eventName,
      },
      extra: data,
    })
  } catch {
    // Falha de observabilidade não deve afetar o fluxo clínico.
  }
}
